'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { createNotification } from '@/lib/notifications';
import EditorialCard from './EditorialCard';
import LookbookCard from './LookbookCard';
import ProductModal from './ProductModal';
import { Loader2 } from 'lucide-react';

export interface LookbookTag {
  id: string;
  post_id: string;
  item_name: string;
  brand: string;
  price: string | null;
  product_url: string | null;
  x_position: number;
  y_position: number;
}

export interface FeedPost {
  id: string;
  post_type: 'editorial' | 'lookbook';
  title: string | null;
  body: string | null;
  cover_image_url: string | null;
  outfit_label: string | null;
  lookbook_image_url: string | null;
  tags: string[];
  like_count: number;
  comment_count: number;
  save_count: number;
  tag_click_count: number;
  created_at: string;
  author: {
    id: string;
    display_name: string | null;
    username: string | null;
    avatar_url: string | null;
    role: string;
  };
  lookbook_items?: LookbookTag[];
  is_liked?: boolean;
  is_saved?: boolean;
}

interface Props {
  filter: 'all' | 'editorial' | 'lookbook';
}

export default function FeedList({ filter }: Props) {
  const { user } = useAuth();
  const supabase = createClient();
  const [posts, setPosts] = useState<FeedPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTag, setActiveTag] = useState<LookbookTag | null>(null);
  const [likedPosts, setLikedPosts] = useState<Set<string>>(new Set());
  const [savedPosts, setSavedPosts] = useState<Set<string>>(new Set());

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let query = supabase
        .from('posts')
        .select(`
          id, post_type, title, body, cover_image_url, outfit_label,
          lookbook_image_url, tags, like_count, comment_count, save_count,
          tag_click_count, created_at,
          author:profiles!posts_author_id_fkey(id, display_name, username, avatar_url, role),
          lookbook_items(id, post_id, item_name, brand, price, product_url, x_position, y_position)
        `)
        .eq('is_hidden', false)
        .order('created_at', { ascending: false })
        .limit(20);

      if (filter !== 'all') {
        query = query.eq('post_type', filter);
      }

      const { data, error: fetchError } = await query;
      if (fetchError) throw fetchError;
      setPosts((data as any[]) || []);

      // Fetch user interactions if logged in
      if (user && data?.length) {
        const postIds = data.map((p: any) => p.id);
        const [likesRes, savesRes] = await Promise.all([
          supabase.from('post_likes').select('post_id').eq('user_id', user.id).in('post_id', postIds),
          supabase.from('saved_posts').select('post_id').eq('user_id', user.id).in('post_id', postIds),
        ]);
        setLikedPosts(new Set(likesRes.data?.map((l: any) => l.post_id) || []));
        setSavedPosts(new Set(savesRes.data?.map((s: any) => s.post_id) || []));
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to load posts');
    } finally {
      setLoading(false);
    }
  }, [filter, user?.id]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const handleLike = async (postId: string) => {
    if (!user) return;
    const isLiked = likedPosts.has(postId);
    setLikedPosts(prev => {
      const next = new Set(prev);
      isLiked ? next.delete(postId) : next.add(postId);
      return next;
    });
    setPosts(prev => prev.map(p => p.id === postId ? { ...p, like_count: p.like_count + (isLiked ? -1 : 1) } : p));
    if (isLiked) {
      await supabase.from('post_likes').delete().eq('post_id', postId).eq('user_id', user.id);
    } else {
      await supabase.from('post_likes').insert({ post_id: postId, user_id: user.id });
      // Notify post author
      const post = posts.find(p => p.id === postId);
      if (post?.author?.id) {
        await createNotification({
          recipientId: post.author.id,
          actorId: user.id,
          type: 'like',
          postId,
        });
      }
    }
  };

  const handleSave = async (postId: string) => {
    if (!user) return;
    const isSaved = savedPosts.has(postId);
    setSavedPosts(prev => {
      const next = new Set(prev);
      isSaved ? next.delete(postId) : next.add(postId);
      return next;
    });
    setPosts(prev => prev.map(p => p.id === postId ? { ...p, save_count: p.save_count + (isSaved ? -1 : 1) } : p));
    if (isSaved) {
      await supabase.from('saved_posts').delete().eq('post_id', postId).eq('user_id', user.id);
    } else {
      await supabase.from('saved_posts').insert({ post_id: postId, user_id: user.id });
      // Notify post author
      const post = posts.find(p => p.id === postId);
      if (post?.author?.id) {
        await createNotification({
          recipientId: post.author.id,
          actorId: user.id,
          type: 'save',
          postId,
        });
      }
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-3">
        <Loader2 size={28} className="animate-spin text-charcoal-400" />
        <p className="text-sm text-charcoal-500">Loading the runway…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-2xl border border-cream-300 p-10 text-center">
        <p className="text-charcoal-500 text-sm mb-3">{error}</p>
        <button onClick={fetchPosts} className="px-4 py-2 bg-charcoal-900 text-white text-sm rounded-lg hover:bg-charcoal-800 transition-colors">
          Try again
        </button>
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-cream-300 p-12 text-center">
        <div className="w-16 h-16 bg-cream-200 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <span className="text-2xl">✨</span>
        </div>
        <h3 className="font-editorial text-lg font-semibold text-charcoal-900 mb-2">The runway is empty</h3>
        <p className="text-sm text-charcoal-500 max-w-xs mx-auto">
          {filter === 'all' ? 'Be the first to publish an editorial or lookbook.' : `No ${filter} posts yet. Be the first!`}
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6">
        {posts.map((post) =>
          post.post_type === 'editorial' ? (
            <EditorialCard
              key={post.id}
              post={post}
              isLiked={likedPosts.has(post.id)}
              isSaved={savedPosts.has(post.id)}
              onLike={() => handleLike(post.id)}
              onSave={() => handleSave(post.id)}
              isLoggedIn={!!user}
            />
          ) : (
            <LookbookCard
              key={post.id}
              post={post}
              isLiked={likedPosts.has(post.id)}
              isSaved={savedPosts.has(post.id)}
              onLike={() => handleLike(post.id)}
              onSave={() => handleSave(post.id)}
              onTagClick={setActiveTag}
              isLoggedIn={!!user}
            />
          )
        )}
      </div>
      {activeTag && (
        <ProductModal tag={activeTag} onClose={() => setActiveTag(null)} />
      )}
    </>
  );
}